// CTABanner · the sitewide closer placed above the Footer on every page.
//
// Institutional 2-column design (team feedback: the previous centered
// "Let's get you on the markets" headline was too generic for a broker
// of this seriousness).
//
//  LEFT  · Pitch · bolder, time-anchored headline ("From sign-up to first
//          trade in under 4 hours."), proof-line CTA copy, the original two
//          buttons (Open Account · Try Demo), trust-signals row.
//  RIGHT · Account-opening flow in four numbered steps (Sign-up → KYC →
//          Fund → Trade) + a live spreads strip that polls /api/market/quotes
//          via the existing useLivePrices hook, so the closer ends with
//          actual broker data — not marketing copy.
//
// Visual rules · institutional aesthetic
//   • card-beam shell, NO neon glow, accent opacity ≤ 14 %.
//   • Outfit font (global).
//   • Tabular nums for every numeric (spread, decimals, step indices).
//   • Hairline accent strip slides in from left along the bottom on hover.
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, ShieldCheck, Wallet, BadgeCheck, Clock,
  UserPlus, FileCheck2, LineChart, Activity,
} from "lucide-react";
import useLivePrices from "@/hooks/useLivePrices";
import { useCtaTarget } from "@/lib/useCtaTarget";

/* Steps · four-stage account-opening flow. Labels + sub-copy flow
   through i18n so every locale renders natively. Eta strings that are
   universal (numeric/symbolic) stay literal. */
const STEPS_META = [
  { Icon: UserPlus,    labelKey: "common.signUp",       subKey: "ui.ctaBanner.signupSub", eta: "2 min" },
  { Icon: FileCheck2,  labelKey: "common.verifyKyc",    subKey: "ui.ctaBanner.verifySub", eta: "<4 h" },
  { Icon: Wallet,      labelKey: "common.fundAccount",  subKey: "ui.ctaBanner.fundSub",   etaKey: "ui.ctaBanner.fundEta" },
  { Icon: LineChart,   labelKey: "common.tradeMarkets", subKey: "ui.ctaBanner.tradeSub",  eta: "0.0 pips" },
];

/* Trust strip · 4 institutional credentials shown beneath the CTAs.
   Wording flows through i18n via the existing common.trust* keys. */
const TRUSTS_META = [
  { Icon: ShieldCheck, labelKey: "common.trustLicensed"   },
  { Icon: BadgeCheck,  labelKey: "common.trustSegregated" },
  { Icon: ShieldCheck, labelKey: "common.trustNegBalance" },
  { Icon: Clock,       labelKey: "common.trustSameDay"    },
];

/* Three flagship symbols shown in the live spread strip. We pick one
   from each major asset class so the strip reads as "we move across
   the whole market" without overloading the panel. */
const SPREAD_SYMBOLS = ["EURUSD", "XAUUSD", "BTCUSD"];

/* Spread formatter · mirrors LiveSpreadsPanel logic so the value reads
   in the same unit everywhere on the site. Returns { value, unit }.
   Adds explicit handling for spot metals (XAU/XAG/XPT/XPD) — those
   contain "USD" in their 6-char symbol but pip = $0.10 not $0.0001. */
const INDEX_PATTERN = /^(WS30|SP500|NAS100|GER40|DAX40|UK100|FTSE100|JPN225|NIKKEI|HSI50|HK50|FRA40|CAC40|AUS200|ASX200|AEX25|SWI20|SMI20|ESP35|IBEX35|ITA40|NETH25|EU50|ESX50|SA40|RUS2K|CHINA50|IND50|SPX500)/;
const METAL_PATTERN = /^(XAU|XAG|XPT|XPD)/;
function formatSpread(quote, sym) {
  if (!quote || !Number.isFinite(quote.bid) || !Number.isFinite(quote.ask)) {
    return { value: "—", unit: "" };
  }
  const diff = quote.ask - quote.bid;
  if (/JPY$/.test(sym))           return { value: (diff * 100).toFixed(1),   unit: "pip" };
  if (INDEX_PATTERN.test(sym))    return { value: diff.toFixed(2),           unit: "pts" };
  if (METAL_PATTERN.test(sym))    return { value: (diff * 10).toFixed(1),    unit: "pip" };
  if (sym === "BTCUSD" || sym === "ETHUSD")
                                  return { value: `$${diff.toFixed(0)}`,     unit: "spread" };
  if (sym.length === 6)           return { value: (diff * 10000).toFixed(1), unit: "pip" };
  return { value: diff.toFixed(2), unit: "" };
}

function LiveSpreadTile({ symbol, quote }) {
  const { value: spreadValue, unit } = formatSpread(quote, symbol);
  const last = quote?.bid != null ? Number(quote.bid).toFixed(quote.bid > 100 ? 2 : 5) : "—";
  return (
    <div
      className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg"
      style={{
        background: "color-mix(in srgb, var(--brand) 4%, var(--surface))",
        border: "1px solid color-mix(in srgb, var(--brand) 10%, var(--line))",
      }}
      data-testid={`cta-spread-${symbol.toLowerCase()}`}
    >
      <div className="flex items-center gap-2">
        <span className="relative flex h-1.5 w-1.5">
          <span
            className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
            style={{ background: "color-mix(in srgb, var(--brand) 70%, transparent)" }}
          />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: "var(--brand)" }} />
        </span>
        <span className="font-mono text-[12px] uppercase tracking-[0.15em] text-text-2">{symbol}</span>
      </div>
      <div className="flex items-baseline gap-3">
        <span className="font-mono tabular-nums text-[12px] text-text-1">{last}</span>
        <span
          className="font-mono tabular-nums text-[12px] px-1.5 py-0.5 rounded whitespace-nowrap"
          style={{
            color: "var(--brand)",
            background: "color-mix(in srgb, var(--brand) 8%, transparent)",
          }}
        >
          {spreadValue}{unit ? ` ${unit}` : ""}
        </span>
      </div>
    </div>
  );
}

export default function CTABanner() {
  const { t } = useTranslation();
  const { prices, status } = useLivePrices();
  const isLive = status === "connected" || status === "fallback";
  const ctaBanner = useCtaTarget("cta_banner");
  // Hide the "Try Demo" CTA on the social account page · social copy-
  // trading runs through the Trader's Room live-only flow, no demo equivalent.
  const { pathname } = useLocation();
  const isSocial = pathname === "/account-types/social";

  // Build the localised STEPS + TRUSTS arrays per-render so the active
  // locale (and therefore each label / sub-copy) is always up to date.
  const STEPS = STEPS_META.map((s) => ({
    Icon: s.Icon,
    label: t(s.labelKey),
    sub: t(s.subKey),
    eta: s.etaKey ? t(s.etaKey) : s.eta,
  }));
  const TRUSTS = TRUSTS_META.map((tr) => ({ Icon: tr.Icon, label: t(tr.labelKey) }));

  return (
    <section data-testid="cta-banner" className="relative py-14 lg:py-24 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="card-beam shine-sweep relative p-6 sm:p-8 lg:p-12 overflow-hidden group"
          style={{ "--shine-color": "var(--brand)" }}
        >
          {/* Subtle radial accent · NO neon, kept under 14 % opacity */}
          <div
            aria-hidden
            className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, color-mix(in srgb, var(--brand) 14%, transparent), transparent 70%)",
              filter: "blur(12px)",
            }}
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start relative">
            {/* === LEFT · Pitch + CTAs + trust signals === */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 pill-mono mb-5"
                data-testid="cta-banner-pill"
              >
                <Sparkles size={11} className="text-brand" />
                {t("cta.readyPill", "Ready to trade live?")}
              </motion.div>

              <h2
                data-testid="cta-banner-headline"
                className="font-display text-[2.25rem]/[1.12] sm:text-5xl/[1.1] lg:text-[3.5rem]/[1.08] xl:text-6xl/[1.05] font-medium tracking-[-0.02em] text-text-1"
              >
                {t("cta.title", "From sign-up to")}{" "}
                <span className="text-gradient-brand font-semibold">
                  {t("cta.titleAccent", "first trade in under 4 hours.")}
                </span>
              </h2>

              <p
                data-testid="cta-banner-subtitle"
                className="text-text-2 mt-6 max-w-xl mx-auto lg:mx-0 text-[14.5px] lg:text-base font-light leading-relaxed"
              >
                {t(
                  "cta.subtitle",
                  "Open a live MT5 account, complete KYC and start trading 1200+ instruments on raw spreads from 0.0 pips · the same execution stack institutional desks use, with a dedicated account manager when you need one."
                )}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center lg:justify-start gap-3 mt-9">
                <motion.a
                  {...ctaBanner.linkProps}
                  data-testid="cta-banner-primary"
                  whileTap={{ scale: 0.98 }}
                  className="btn-gloss btn-solid-brand inline-flex items-center justify-center gap-2 h-12 px-7 rounded-xl text-[14px] font-semibold"
                >
                  {t("cta.primary", "Open Account")}
                  <ArrowRight size={14} />
                </motion.a>
                {!isSocial && (
                  <motion.a
                    href="https://demo.ofinancial.markets/"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="cta-banner-secondary"
                    whileTap={{ scale: 0.98 }}
                    className="btn-gloss-ghost inline-flex items-center justify-center gap-2 h-12 px-7 text-[14px] font-medium rounded-xl"
                    style={{ color: "var(--text-1)" }}
                  >
                    {t("cta.secondary", "Try Demo")}
                  </motion.a>
                )}
              </div>

              {/* Trust signals · 4-up row · text wraps to 2 lines on
                  narrow desktops so phrases like "Negative balance
                  protection" and "Withdrawals same business day" can
                  show in full instead of being truncated with "...". */}
              <ul
                data-testid="cta-banner-trust"
                className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 mt-10 pt-7 border-t"
                style={{ borderColor: "var(--line)" }}
              >
                {TRUSTS.filter(t => !isSocial || !t.label.includes("FSC")).map(({ Icon, label }) => (
                  <li key={label} className="flex items-start gap-2 min-w-0">
                    <Icon size={13} strokeWidth={1.8} style={{ color: "var(--brand)", marginTop: 3 }} className="shrink-0" />
                    <span className="text-text-3 text-[12px] leading-snug font-light break-words">{label}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* === RIGHT · Account opening steps + live spreads === */}
            <div className="lg:col-span-5">
              <div
                className="rounded-2xl p-5 sm:p-6 relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(180deg, color-mix(in srgb, var(--brand) 3%, var(--surface-2)), var(--surface-2))",
                  border: "1px solid color-mix(in srgb, var(--brand) 8%, var(--line))",
                }}
                data-testid="cta-banner-flow"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-text-3">
                    {t("common.openInFourSteps")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="relative flex h-1.5 w-1.5">
                      {isLive && (
                        <span
                          className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                          style={{ background: "color-mix(in srgb, var(--brand) 70%, transparent)" }}
                        />
                      )}
                      <span
                        className="relative inline-flex rounded-full h-1.5 w-1.5"
                        style={{ background: isLive ? "var(--brand)" : "var(--text-3)" }}
                      />
                    </span>
                    <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-text-3">
                      {isLive ? t("common.liveFeed") : t("ui.ctaBanner.connecting")}
                    </span>
                  </span>
                </div>

                {/* 4 numbered steps */}
                <ol className="space-y-3.5">
                  {STEPS.map((s, i) => (
                    <motion.li
                      key={s.label}
                      initial={{ opacity: 0, x: 12 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.15 + i * 0.07 }}
                      className="flex items-center gap-3.5"
                      data-testid={`cta-step-${i}`}
                    >
                      <div
                        className="shrink-0 w-9 h-9 rounded-lg grid place-items-center"
                        style={{
                          background: "color-mix(in srgb, var(--brand) 8%, transparent)",
                          border: "1px solid color-mix(in srgb, var(--brand) 14%, transparent)",
                        }}
                      >
                        <s.Icon size={15} strokeWidth={1.8} style={{ color: "var(--brand)" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 tabular-nums">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span className="font-display text-[13px] font-medium text-text-1 truncate">
                            {s.label}
                          </span>
                        </div>
                        <p className="text-text-3 text-[12px] font-light leading-snug mt-0.5">{s.sub}</p>
                      </div>
                      <span
                        className="font-mono tabular-nums text-[12px] px-2 py-0.5 rounded-md shrink-0"
                        style={{
                          color: "var(--brand)",
                          background: "color-mix(in srgb, var(--brand) 7%, transparent)",
                          border: "1px solid color-mix(in srgb, var(--brand) 12%, transparent)",
                        }}
                      >
                        {s.eta}
                      </span>
                    </motion.li>
                  ))}
                </ol>

                {/* Live spread strip · 3 flagship symbols */}
                <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--line)" }}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Activity size={11} className="text-text-3" />
                    <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-text-3">
                      {t("ui.ctaBanner.liveSpreadsNow")}
                    </span>
                  </div>
                  <div className="grid gap-1.5">
                    {SPREAD_SYMBOLS.map((sym) => (
                      <LiveSpreadTile key={sym} symbol={sym} quote={prices?.[sym]} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom accent line · institutional polish (kept under 35 %) */}
          <span
            aria-hidden
            className="absolute left-0 bottom-0 h-px w-0 transition-[width] duration-700 ease-out group-hover:w-full"
            style={{ background: "linear-gradient(90deg, var(--brand), transparent)", opacity: 0.55 }}
          />
        </motion.div>
      </div>
    </section>
  );
}
